import React from "react";
import { Button } from "@/components/ui/button";
import { PhoneCall, Video } from "lucide-react";
import { CallBarTimer, LawyerWaitCountdown } from "@shared/consultation/SessionTimer";

// Main stage for audio/video consultations when not in the call: join / rejoin / locked / completed.
export function CallStage({ consult, isLive, isCompleted, isLawyerSide, hadCall, onJoin, onExpired, onGift, onWaitExpired }) {
  const video = consult.consultation_type === "video";
  const other = isLawyerSide ? consult.user_name : consult.lawyer_name;
  const Icon = video ? Video : PhoneCall;
  return (
    <div className="box-border flex w-full max-w-full min-w-0 flex-1 min-h-0 flex-col bg-[#0A2540] text-white" data-testid="call-stage-idle">
      {/* Same bar as the in-call screen so the one consultation timer stays visible while not connected */}
      {isLive && consult.payment_status === "paid" && (
        <div className="shrink-0 px-4 sm:px-5 py-3 flex flex-wrap items-center justify-between gap-2 sm:gap-3 bg-black/30" data-testid="call-stage-bar">
          <span className="text-xs font-bold tracking-wider uppercase text-white/70">{video ? "Video" : "Audio"} · Not connected</span>
          <CallBarTimer kind={video ? "Video" : "Audio"} timer={consult.timer} onExpired={onExpired} canGift={isLawyerSide} onGift={onGift} />
        </div>
      )}
      <div className="box-border grid w-full max-w-full min-w-0 flex-1 min-h-0 place-items-center overflow-y-auto p-4 sm:p-6">
      <div className="w-full max-w-sm min-w-0 text-center">
        <div className="mx-auto h-24 w-24 rounded-full bg-white/10 grid place-items-center text-3xl font-extrabold">
          {(other || "?")[0]}
        </div>
        <p className="mt-4 break-words text-lg font-extrabold [overflow-wrap:anywhere]" style={{ fontFamily: "Poppins" }}>{other}</p>
        <p className="text-xs text-white/60 capitalize">{consult.consultation_type} consultation</p>

        {!isCompleted && consult.payment_status === "paid" && isLive && (
          <Button onClick={onJoin} data-testid="start-call-btn"
            className={`mt-6 h-12 px-8 rounded-full font-bold ${video ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "adv-btn-gold"}`}>
            <Icon size={16} className="mr-2" /> {hadCall ? "Rejoin" : "Join"} {consult.consultation_type} call
          </Button>
        )}
        {isLive && consult.payment_status === "paid" && !consult.timer?.started_at && consult.lawyer_no_show?.stage === "initiation" && (
          <LawyerWaitCountdown noShow={consult.lawyer_no_show} onExpired={onWaitExpired} isLawyerSide={isLawyerSide} />
        )}
        {!isCompleted && consult.payment_status === "paid" && !isLive && !isLawyerSide && (
          <LawyerWaitCountdown noShow={consult.lawyer_no_show} onExpired={onWaitExpired} />
        )}
        {!isCompleted && consult.payment_status === "paid" && !isLive && (
          <div className="mt-6 rounded-xl border border-dashed border-white/25 bg-white/5 px-4 py-3 text-xs text-white/80 flex items-start gap-2 text-left" data-testid="call-locked-note">
            <Icon size={14} className="mt-0.5 shrink-0 text-white/50" />
            <span>
              {isLawyerSide
                ? `Press Start consultation to unlock the ${consult.consultation_type} call.`
                : `The ${consult.consultation_type} call unlocks when ${consult.lawyer_name || "the lawyer"} starts the consultation — we've notified them; this page updates automatically.`}
            </span>
          </div>
        )}
        {!isCompleted && consult.payment_status !== "paid" && (
          <p className="mt-6 text-xs text-white/70" data-testid="call-stage-unpaid">Complete payment to unlock the call.</p>
        )}
      </div>
      </div>
    </div>
  );
}
